import * as THREE from 'three';
import { CLASSICAL, INDUSTRIAL } from '../materials';
import { Beam, Figure, type ModelProps, Pole, Squad } from '../parts';

/** Industrial-era units: each sits on y = 0, faces +z, at real proportions (UnitModel scales them). */

const { DARK_WOOD, SKIN, WOOD } = CLASSICAL;
const { STEEL, IRON, SOOT, SLATE, TYRE, HARD_HAT, REFLECTOR, DENIM, SACK } = INDUSTRIAL;

const G = {
  // Rifle squad
  capCrown: new THREE.CylinderGeometry(0.07, 0.066, 0.04, 10),
  capPeak: new THREE.BoxGeometry(0.1, 0.012, 0.06),
  // Artillery
  wheelRim: new THREE.TorusGeometry(0.11, 0.018, 5, 16).rotateY(Math.PI / 2),
  spoke: new THREE.BoxGeometry(0.014, 0.2, 0.014),
  hub: new THREE.CylinderGeometry(0.028, 0.028, 0.05, 8).rotateZ(Math.PI / 2),
  axle: new THREE.BoxGeometry(0.3, 0.025, 0.025),
  trail: new THREE.BoxGeometry(0.06, 0.04, 0.42),
  spade: new THREE.BoxGeometry(0.1, 0.05, 0.02),
  cradle: new THREE.BoxGeometry(0.08, 0.07, 0.2),
  barrel: new THREE.CylinderGeometry(0.018, 0.024, 0.46, 8),
  muzzle: new THREE.CylinderGeometry(0.026, 0.026, 0.04, 8),
  gunShield: new THREE.BoxGeometry(0.3, 0.2, 0.015),
  ammoBox: new THREE.BoxGeometry(0.1, 0.06, 0.08),
  // Labourer
  torso: new THREE.CylinderGeometry(0.1, 0.13, 0.3, 8),
  head: new THREE.SphereGeometry(0.075, 10, 8),
  vest: new THREE.CylinderGeometry(0.108, 0.124, 0.17, 8),
  vestBand: new THREE.CylinderGeometry(0.111, 0.117, 0.022, 8),
  hardHat: new THREE.SphereGeometry(0.084, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2),
  hatBrim: new THREE.CylinderGeometry(0.105, 0.105, 0.012, 12),
  tray: new THREE.CylinderGeometry(0.13, 0.08, 0.08, 4, 1, true).rotateY(Math.PI / 4),
  trayFloor: new THREE.BoxGeometry(0.11, 0.012, 0.11),
  barrowWheel: new THREE.CylinderGeometry(0.055, 0.055, 0.03, 10).rotateZ(Math.PI / 2),
  sack: new THREE.SphereGeometry(0.07, 8, 6),
  shovelBlade: new THREE.BoxGeometry(0.06, 0.07, 0.012),
};

// The rifle, slung diagonally across the chest: its axis (a Pole tilted by
// RIFLE_TILT about z) and the steel barrel continuing past the stock's top end.
const RIFLE_TILT = -0.8;
const RIFLE_DIR = [-Math.sin(RIFLE_TILT), Math.cos(RIFLE_TILT)];
const RIFLE_POS: [number, number, number] = [0.02, 0.24, 0.1];
const RIFLE_STOCK = 0.34;
const RIFLE_BARREL = 0.13;
const BARREL_POS: [number, number, number] = [
  RIFLE_POS[0] + RIFLE_DIR[0] * (RIFLE_STOCK + RIFLE_BARREL) * 0.5,
  RIFLE_POS[1] + RIFLE_DIR[1] * (RIFLE_STOCK + RIFLE_BARREL) * 0.5,
  RIFLE_POS[2],
];

// The wheelbarrow in front of the labourer: its tray centre, wheel and the
// handle grips at his hands.
const TRAY: [number, number, number] = [0, 0.15, 0.27];
const BARROW_WHEEL: [number, number, number] = [0, 0.055, 0.4];
const GRIP_Z = 0.1;

/**
 * A worker's upgrade: a stockier figure in denim with a team-coloured work
 * vest (reflective band), a yellow hard hat, pushing a steel wheelbarrow with
 * a full sack and a shovel in it.
 */
export function Labourer({ accent }: ModelProps) {
  return (
    <>
      <mesh geometry={G.torso} material={DENIM} position={[0, 0.15, 0]} />
      <mesh geometry={G.vest} material={accent} position={[0, 0.2, 0]} />
      <mesh geometry={G.vestBand} material={REFLECTOR} position={[0, 0.19, 0]} />
      <mesh geometry={G.head} material={SKIN} position={[0, 0.37, 0]} />
      <mesh geometry={G.hardHat} material={HARD_HAT} position={[0, 0.39, 0]} />
      <mesh geometry={G.hatBrim} material={HARD_HAT} position={[0, 0.395, 0.01]} />
      {[-0.08, 0.08].map((x) => (
        <group key={x}>
          <Beam from={[x, 0.2, GRIP_Z]} to={[x * 0.8, TRAY[1], TRAY[2]]} material={WOOD} />
          <Beam
            from={[x * 0.7, TRAY[1] - 0.03, TRAY[2] - 0.03]}
            to={[x * 0.7, 0, TRAY[2] - 0.05]}
            material={IRON}
          />
        </group>
      ))}
      <mesh geometry={G.tray} material={STEEL} position={TRAY} />
      <mesh geometry={G.trayFloor} material={IRON} position={[TRAY[0], TRAY[1] - 0.035, TRAY[2]]} />
      <Beam from={[0, TRAY[1] - 0.03, TRAY[2] + 0.04]} to={BARROW_WHEEL} material={IRON} />
      <mesh geometry={G.barrowWheel} material={TYRE} position={BARROW_WHEEL} />
      <mesh
        geometry={G.sack}
        material={SACK}
        position={[0.01, TRAY[1] + 0.04, TRAY[2] - 0.01]}
        scale={[1, 0.8, 1.1]}
      />
      <Pole length={0.3} position={[-0.04, TRAY[1] + 0.1, TRAY[2] - 0.02]} tilt={0.5} />
      <mesh
        geometry={G.shovelBlade}
        material={STEEL}
        position={[-0.11, TRAY[1] + 0.22, TRAY[2] - 0.02]}
        rotation={[0, 0, 0.5]}
      />
    </>
  );
}

/** A peaked cap (drawn in a figure's frame). */
function PeakedCap() {
  return (
    <>
      <mesh geometry={G.capCrown} material={SLATE} position={[0, 0.43, 0]} />
      <mesh geometry={G.capPeak} material={SOOT} position={[0, 0.415, 0.06]} />
    </>
  );
}

/** Three riflemen in team-coloured coats and peaked caps, rifles across their chests. */
export function RifleSquad({ accent }: ModelProps) {
  return (
    <Squad accent={accent} scale={0.75} spread={0.17}>
      <PeakedCap />
      <Pole length={RIFLE_STOCK} position={RIFLE_POS} tilt={RIFLE_TILT} material={DARK_WOOD} />
      <Pole length={RIFLE_BARREL} position={BARREL_POS} tilt={RIFLE_TILT} material={STEEL} />
    </Squad>
  );
}

const WHEEL_Y = 0.11;
const WHEEL_X = 0.15;
// The barrel's elevation above horizontal, and its breech on the cradle.
const ELEVATION = 0.18;
const BREECH: [number, number, number] = [0, 0.18, -0.02];
const BARREL_LENGTH = 0.46;
const barrelAt = (d: number): [number, number, number] => [
  BREECH[0],
  BREECH[1] + Math.sin(ELEVATION) * d,
  BREECH[2] + Math.cos(ELEVATION) * d,
];

/** A spoked iron-rimmed gun wheel at `x`, turned to roll along z. */
function GunWheel({ x }: { x: number }) {
  return (
    <group position={[x, WHEEL_Y, 0]}>
      <mesh geometry={G.wheelRim} material={IRON} />
      {[0, Math.PI / 3, -Math.PI / 3].map((a) => (
        <mesh key={a} geometry={G.spoke} material={DARK_WOOD} rotation={[a, 0, 0]} />
      ))}
      <mesh geometry={G.hub} material={SOOT} />
    </group>
  );
}

/**
 * A wheeled field gun: a long steel barrel raised over a split trail that
 * rests on the ground behind, a team-coloured gun shield in front of the
 * axle, an ammunition box on the trail and one capped gunner beside it.
 */
export function Artillery({ accent }: ModelProps) {
  return (
    <>
      {[-WHEEL_X, WHEEL_X].map((x) => (
        <GunWheel key={x} x={x} />
      ))}
      <mesh geometry={G.axle} material={IRON} position={[0, WHEEL_Y, 0]} />
      <mesh
        geometry={G.trail}
        material={IRON}
        position={[0, 0.06, -0.2]}
        rotation={[-0.26, 0, 0]}
      />
      <mesh geometry={G.spade} material={SOOT} position={[0, 0.025, -0.41]} />
      <mesh geometry={G.cradle} material={IRON} position={[0, 0.15, 0.01]} />
      <mesh
        geometry={G.barrel}
        material={STEEL}
        position={barrelAt(BARREL_LENGTH / 2)}
        rotation={[Math.PI / 2 - ELEVATION, 0, 0]}
      />
      <mesh
        geometry={G.muzzle}
        material={SOOT}
        position={barrelAt(BARREL_LENGTH)}
        rotation={[Math.PI / 2 - ELEVATION, 0, 0]}
      />
      <mesh
        geometry={G.gunShield}
        material={accent}
        position={[0, 0.21, 0.08]}
        rotation={[-0.15, 0, 0]}
      />
      <mesh geometry={G.ammoBox} material={DARK_WOOD} position={[0.1, 0.03, -0.26]} />
      <group position={[-0.2, 0, -0.16]} scale={0.7}>
        <Figure accent={accent} />
        <PeakedCap />
      </group>
    </>
  );
}
